<template>
  <div class="full-page-width relative locations" v-if="get_templates.entity.length > 0">
    <ConfirmationBar
      :showSavebar="showSavebar"
      @confirm="saveTemplate"
      @cancel="cancel"
    />
    <AutoMapModal
      :visible="showAutoMapModal"
      :templateType="templateType"
      @close="closeAutoMapModal"
      @apply="applyAutoMapSuggestions"
    />
    <div class="add-button c-float m-b-25">
      <div class="flex-center float-left">
        <Btn
          @click.native="routeIt"
          class="light bordered empty d-inline-block m-r-15 fix-40"
          :icon="{
            name: 'arrowLeftBig',
            size: '20',
          }" />
        <p class="user-name">{{ `${$t('ftp')}` }}</p>
      </div>
    </div>
    <div class="row m-b-40" v-if="get_user.is_admin">
      <div class="col-xs-4 text-box"></div>
      <div class="col-xs-8">
        <div class="card row p-40">
          <div class="col-xs-12">
            <InputSelect
              class="m-b-20"
              :multiSelect=true
              :preselected="accessibleForSubaccounts"
              :label="$t('accessible_for_accounts')"
              @change="selectAccessibleSubaccount"
              :rules="{required: true}"
              :options="getAllowedSubaccounts" />
          </div>
        </div>
      </div>
    </div>
    <div class="row m-b-40">
      <div class="col-xs-4 text-box">
        <InfoTextBox
          :title="$t('edit_ftp_template')"
          :text="$t('ftp-view-info-text-config')"
          :btnText="$t('Delete template')"
          btnType="red"
          btnIcon="deleteForever"
          @action="deleteTemplate" />
      </div>
      <div class="col-xs-8">
        <div class="card row p-40">
          <div class="col-xs-12 toggle-card m-b-30">
            <div>
              <span class="title">{{ $t('status') }}</span>
              <span class="additional-info">{{ `${$t('enable')} ${$t('template')}` }}</span>
            </div>
            <Toggle id="isFtpTemplateActive" v-model="isActive" />
          </div>
          <div class="col-xs-4">
            <InputText
              class="m-b-40"
              :label="$t('Template name')"
              :rules="{required: true}"
              v-model="templateName" />
          </div>
          <div class="col-xs-4">
            <InputSelect
              :label="$t('Start row')"
              v-model="startRow"
              :rules="{required: true}"
              :hint="'start_row_hint'"
              :options="rowData" />
          </div>
          <div class="col-xs-4">
            <InputSelect
              :label="$t('data_separator')"
              v-model="separator"
              :rules="{required: true}"
              :options="get_template_field('template_separator')" />
          </div>
        </div>
      </div>
    </div>
    <hr class="m-b-40"/>
    <div class="row" v-if="templateType !== ''">
      <div class="col-xs-4 text-box">
        <InfoTextBox
          :text="$t('import-templates-view-info-text-map')"
          :title="$t('Select template fields')" />
        <div class="template-action-btns m-t-20">
          <Btn
            @click.native="openAutoMapModal"
            class="brand"
            :icon="{
              name: 'sparkle',
              size: '18',
            }"
            :btnText="$t('auto_map')"/>
          <Btn
            v-if="parseInt(readingType) === 1"
            @click.native="downloadCsv"
            class="dark"
            :icon="{
              name: 'cloudDownload',
              size: '18',
            }"
            :btnText="$t('download_csv')"/>
        </div>
      </div>
      <div class="col-xs-8">
        <div class="card row p-40">
          <!-- Colli/Content count inputs for shipment fields -->
          <div v-if="isShipmentFields" class="col-xs-12 m-b-30">
            <div class="row">
              <div class="col-xs-3">
                <InputSelect
                  :label="$t('Number of colli')"
                  v-model="colliCount"
                  @change="onColliContentChange"
                  :options="colliOptions" />
              </div>
              <div class="col-xs-3">
                <InputSelect
                  :label="$t('Content per colli')"
                  v-model="contentCount"
                  @change="onColliContentChange"
                  :options="contentOptions" />
              </div>
              <div class="col-xs-6" v-if="isLoadingFields">
                <p class="loading-fields-text">{{ $t('Loading fields...') }}</p>
              </div>
            </div>
          </div>
          <!-- All fields in virtual scroller -->
          <div class="col-xs-12">
            <div class="row m-b-10">
              <div class="col-xs-5">
                <h2 class="c-title">{{ $t('ftp_data_fields_order') }}</h2>
                <small class="c-light">{{ $t('ftp_data_fields_order_text') }}</small>
              </div>
              <div class="col-xs-6">
                <h2 class="c-title">{{ $t('Mapping field') }}</h2>
                <small class="c-light">{{ $t('Select field you will map your data to') }}</small>
              </div>
            </div>

            <RecycleScroller
              class="fields-scroller"
              :items="allFieldsCombined"
              :item-size="65"
              key-field="id"
              v-slot="{ item }">
              <div class="row scroller-row">
                <!-- Required field -->
                <template v-if="item.type === 'required'">
                  <div class="col-xs-5">
                    <InputText
                      v-model="requiredFields[item.field_name]"
                      placeholder="Type in header text from your file" />
                  </div>
                  <div class="col-xs-6">
                    <InputText
                      :value="item.field_name"
                      disabled />
                  </div>
                  <div class="col-xs-1">
                  </div>
                </template>
                <!-- Additional field -->
                <template v-else>
                  <div class="col-xs-5">
                    <InputText
                      v-model="item.file_column"
                      placeholder="Type in header text from your file" />
                  </div>
                  <div class="col-xs-6">
                    <MappingSelect
                      v-model="item.field_name"
                      :options="additionalFieldsOptions"
                      @change="selectField(item.index, $event)" />
                  </div>
                  <div class="col-xs-1 text-center">
                    <Btn
                      @click.native="removeField(item.index)"
                      class="gray btn-delete-field"
                      position="icon-pos-center"
                      :icon="{ name: 'deleteForever', size: '16' }" />
                  </div>
                </template>
              </div>
            </RecycleScroller>
          </div>
          <div class="col-xs-12">
            <Btn
              v-if="!isAddDisabled"
              @click.native="addField"
              class="brand float-right"
              :icon="{
                name: 'plus',
                size: '18',
              }"
              :btnText="$t('add_field')" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
/* eslint-disable */
import { mapGetters } from 'vuex';
import Btn from '@/components/elements/buttons';
import InputText from '@/components/elements/inputs/text.vue';
import InputSelect from '@/components/elements/inputs/select.vue';
import MappingSelect from '@/components/elements/inputs/MappingSelect.vue';
import ConfirmationBar from '@/components/global/confirmationBar';
import InfoTextBox from '@/components/global/infoTextBox';
import AutoMapModal from '@/components/global/AutoMapModal';
import EventBus from '@/eventBus.js';
import formValidator from '@/mixins/formValidator';
import Toggle from '@/components/elements/inputs/toggle.vue';
import { RecycleScroller } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

export default {
  name: 'EditFtpTemplate',
  mixins: [formValidator],
  components: { Btn, InputText, InputSelect, MappingSelect, InfoTextBox, ConfirmationBar, Toggle, AutoMapModal, RecycleScroller },
  data() {
    return {
      accessibleForSubaccounts: [],
      isActive: false,
      templateName: '',
      templateType: '',
      fileType: '',
      readingType: '',
      startRow: 0,
      separator: '',
      requiredFields: {},
      additionalFieldsData: [],
      additionalFieldsOptions: [],
      // Confirmation savebar
      showSavebar: false,
      savebarEnabled: false,
      // Auto Map Modal
      showAutoMapModal: false,
      // Colli/content counts for ftp_shipment_input_fields
      colliCount: 1,
      contentCount: 1,
      dgCount: 1,
      isLoadingFields: false,
    };
  },
  computed: {
    ...mapGetters([
      'get_templates',
      'get_template_field',
      'get_subaccounts',
      'get_user'
    ]),
    getAllowedSubaccounts() {
      return this.get_subaccounts.filter((acc) => this.get_user.accessible_subaccounts_array.indexOf(String(acc.id)) > -1).map((acc) => ({text: acc.name, value: String(acc.id),}));
    },
    rowData() {
      const objArray = [];
      for (let ii = 1; ii < 20; ii++) {
        objArray.push({
          text: ii.toString(),
          value: ii,
        });
      }
      return objArray;
    },
    getTemplateTypes() {
      let result = [];
      for(const property in this.get_templates.entity[0]){
        if( property.includes('_fields') ){
          result.push({
            text: property,
            value: property,
          });
        }
      };
      return result;
    },
    isAddDisabled() {
      return this.additionalFieldsOptions.length == this.additionalFieldsData.length;
    },
    colliOptions() {
      const options = [];
      for (let i = 1; i <= 10; i++) {
        options.push({ text: i.toString(), value: i });
      }
      return options;
    },
    contentOptions() {
      const options = [];
      for (let i = 1; i <= 25; i++) {
        options.push({ text: i.toString(), value: i });
      }
      return options;
    },
    isShipmentFields() {
      return this.templateType === 'ftp_shipment_input_fields';
    },
    allFieldsCombined() {
      const combined = [];
      if (this.templateType && this.get_templates.entity[0] && this.get_templates.entity[0][this.templateType]) {
        this.get_templates.entity[0][this.templateType].forEach((field, idx) => {
          if (field.required) {
            combined.push({
              id: `req-${idx}`,
              type: 'required',
              field_name: field.field,
            });
          }
        });
      }
      // Add additional fields - pass original object reference
      this.additionalFieldsData.forEach((item, idx) => {
        item.id = `add-${idx}`;
        item.type = 'additional';
        combined.push(item);
      });
      return combined;
    },
  },
  created() {
    this.$store.dispatch('GET_TEMPLATES').then(() => {
      this.$store.dispatch('GET_TEMPLATE_BY_ID', this.$route.params.id).then((res) => {
        this.accessibleForSubaccounts = res.items.accessible_for_subaccounts || [];
        this.templateName = res.items.name || '';
        this.isActive = res.items.is_active || false;
        this.templateType = res.items.template_type || '';
        this.fileType = res.items.template_file_type || '';
        this.readingType = res.items.template_reading_type || '';
        this.startRow = res.items.start_from_row || '';
        this.separator = res.items.template_separator || '';

        // Detect max colli/content from saved fields
        let maxColli = 1;
        let maxContent = 1;
        res.items.fields_json.forEach(field => {
          const colliMatch = field.field_name.match(/colli_(\d+)_/);
          if (colliMatch) maxColli = Math.max(maxColli, parseInt(colliMatch[1]));
          const contentMatch = field.field_name.match(/content_(\d+)_/);
          if (contentMatch) maxContent = Math.max(maxContent, parseInt(contentMatch[1]));
        });
        this.colliCount = maxColli;
        this.contentCount = maxContent;

        // If ftp_shipment_input_fields, load dynamic fields with the correct counts
        // then populate additionalFieldsData
        if (res.items.template_type === 'ftp_shipment_input_fields') {
          this.setData(res.items.template_type);
          this.$store.dispatch('GET_DYNAMIC_TEMPLATE_FIELDS', {
            type: res.items.template_type,
            colli: this.colliCount,
            content: this.contentCount,
            dg: this.dgCount,
          }).then(response => {
            if (response && response.fields) {
              this.additionalFieldsOptions = response.fields
                .filter(f => !f.required)
                .map(f => ({ text: f.field, value: f.field, selected: false }));
            }
            // Populate mappings after options are loaded
            this._populateFieldsFromJson(res.items.fields_json);
            this.$nextTick(() => { this.savebarEnabled = true; });
          });
        } else {
          this.setData(res.items.template_type);
          this._populateFieldsFromJson(res.items.fields_json);
          this.$nextTick(() => { this.savebarEnabled = true; });
        }
      });
    });
  },
  methods: {
    _populateFieldsFromJson(fieldsJson) {
      fieldsJson.forEach((item) => {
        let setField = true;
        this.additionalFieldsOptions.forEach((option, index) => {
          if (option.value == item.field_name) {
            this.additionalFieldsOptions[index].selected = true;
            this.additionalFieldsData.push({
              index: this.additionalFieldsData.length,
              field_name: item.field_name,
              file_column: item.file_column
            });
            setField = false;
          }
        });
        if (setField) this.requiredFields[`${item.field_name}`] = item.file_column;
      });
    },
    routeIt() {
      this.$router.go(-1);
    },
    selectAccessibleSubaccount(val) {
        this.accessibleForSubaccounts = val;
    },
    deleteTemplate() {
      this.$store.dispatch('DELETE_TEMPLATE_BY_ID', this.$route.params.id).then(() => {
        this.$router.push('/settings/platform/import-templates');
      });
    },
    cancel() {
      this.showSavebar = false;
    },
    saveTemplate(){
      EventBus.$emit('checkSubmitErrors');
      if(this.get_user.is_admin){
        if(this.accessibleForSubaccounts.length == 0) return;
        this.accessibleForSubaccounts = this.accessibleForSubaccounts.filter((val) => this.get_user.accessible_subaccounts_array.indexOf(String(val)) > -1);
      }
      // CHEK FORM
      let formWithValidators = [
        {
          formFeild:'templateName',
          value: this.templateName,
          validators:{required: true}
        },
        {
          formFeild:'separator',
          value: this.separator,
          validators:{required: true}
        },
      ]

      if(!this.validateInput(formWithValidators)){
        this.$store.dispatch('EDIT_TEMPLATE', {
          id: this.$route.params.id,
          form: {
            accessible_for_subaccounts: this.accessibleForSubaccounts,
            name: this.templateName,
            is_active: this.isActive,
            template_type: this.templateType,
            template_reading_type: 3,//this.readingType,
            template_separator: this.separator,
            template_file_type: 3,//this.fileType,
            sheet_number: 1,
            start_from_row: this.startRow,
            fields_json: this.spreadFields(),
          },
        }).then(() => {
          this.showSavebar = false;
        })
      }
    },
    spreadFields() {
      const fields = [];
      for(const item in this.requiredFields) {
        fields.push({
          field_name: item,
          file_column: this.requiredFields[item],
        });
      }

      this.additionalFieldsData.forEach((obj, i) => {
        fields.push({
          field_name: obj.field_name,
          file_column: obj.file_column,
        });
      });

      return fields;
    },
    addField() {
      this.additionalFieldsData.push({
        index: this.additionalFieldsData.length,
        file_column: '',
        field_name: ''
      });
      this.$nextTick(() => {
        const scroller = this.$el.querySelector('.fields-scroller');
        if (scroller) scroller.scrollTop = scroller.scrollHeight;
      });
    },
    removeField(index) {
      const fieldName = this.additionalFieldsData[index].field_name;
      for(const prop in this.additionalFieldsOptions){
        if(this.additionalFieldsOptions[prop].value == fieldName){
          this.additionalFieldsOptions[prop].selected = false;
        }
      }

      this.additionalFieldsData.splice(index, 1);
      this.additionalFieldsData.forEach((f, i) => { f.index = i; });
    },
    selectField(index, val) {
      //Give a little time to v-model to do the stuff
      setTimeout(() => {
        //Reset additional fields options list
        for(const prop in this.additionalFieldsOptions){
          if(this.additionalFieldsOptions[prop].value != val){
            this.additionalFieldsOptions[prop].selected = false;
          }

          //Mark currently selected field
          if (this.additionalFieldsOptions[prop].value === val){
            this.additionalFieldsOptions[prop].selected = true;
          }

          //Mark already added fields as selected
          for (let i = 0; i < this.additionalFieldsData.length; i++) {
            if (this.additionalFieldsData[i].field_name === this.additionalFieldsOptions[prop].value) {
              this.additionalFieldsOptions[prop].selected = true;
            }
          }
        }
      }, 50)
    },
    setData(val) {
      this.requiredFields = new Object();
      this.additionalFieldsOptions = new Array();
      const obj = this.get_templates.entity[0][val];
      for(let ii=0; ii < obj.length; ii++){
        if( obj[ii].required ){
          this.$set(this.requiredFields, [obj[ii].field], '');
        } else {
          this.additionalFieldsOptions.push({
            text: obj[ii].field,
            value: obj[ii].field,
            selected: false,
          });
        }

      }
    },
    async onColliContentChange() {
      if (!this.isShipmentFields) return;

      this.isLoadingFields = true;
      try {
        const response = await this.$store.dispatch('GET_DYNAMIC_TEMPLATE_FIELDS', {
          type: this.templateType,
          colli: this.colliCount,
          content: this.contentCount,
          dg: this.dgCount,
        });

        if (response && response.fields) {
          // Preserve currently selected fields
          const currentSelections = this.additionalFieldsData.map(f => ({
            field_name: f.field_name,
            file_column: f.file_column,
          }));

          // Rebuild additionalFieldsOptions with new fields
          this.additionalFieldsOptions = [];
          const newRequiredFields = {};

          for (const field of response.fields) {
            if (field.required) {
              // Preserve existing value if available
              const existingValue = this.requiredFields[field.field] || '';
              newRequiredFields[field.field] = existingValue;
            } else {
              const isSelected = currentSelections.some(s => s.field_name === field.field);
              this.additionalFieldsOptions.push({
                text: field.field,
                value: field.field,
                selected: isSelected,
              });
            }
          }

          this.requiredFields = newRequiredFields;

          // Filter additionalFieldsData to only keep valid fields
          this.additionalFieldsData = currentSelections.filter(s =>
            this.additionalFieldsOptions.some(o => o.value === s.field_name)
          ).map((s, idx) => ({
            index: idx,
            field_name: s.field_name,
            file_column: s.file_column,
          }));
        }
      } catch (err) {
        console.error('Failed to load dynamic fields:', err);
      } finally {
        this.isLoadingFields = false;
      }
    },
    openAutoMapModal() {
      this.showAutoMapModal = true;
    },
    closeAutoMapModal() {
      this.showAutoMapModal = false;
    },
    applyAutoMapSuggestions(suggestions) {
      if (!suggestions || !Array.isArray(suggestions)) return;

      suggestions.forEach(suggestion => {
        const fieldName = suggestion.field_name;
        const fileColumn = suggestion.file_column;

        // Check if it's a required field
        if (this.requiredFields.hasOwnProperty(fieldName)) {
          this.$set(this.requiredFields, fieldName, fileColumn);
        } else {
          // Check if field is already in additionalFieldsData
          const existingIndex = this.additionalFieldsData.findIndex(f => f.field_name === fieldName);
          if (existingIndex === -1) {
            // Add new field
            this.additionalFieldsData.push({
              index: this.additionalFieldsData.length,
              file_column: fileColumn,
              field_name: fieldName
            });
            // Mark as selected in options
            const optionIndex = this.additionalFieldsOptions.findIndex(o => o.value === fieldName);
            if (optionIndex !== -1) {
              this.additionalFieldsOptions[optionIndex].selected = true;
            }
          } else {
            // Update existing field
            this.additionalFieldsData[existingIndex].file_column = fileColumn;
          }
        }
      });

      this.showSavebar = true;
    },
    downloadCsv() {
      const fields = this.spreadFields();
      const fileColumns = fields.map(f => f.file_column).filter(col => col);
      const csvContent = fileColumns.join(',');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${this.templateName || 'template'}_headers.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  },
  watch: {
    'accessibleForSubaccounts': {
      handler(){
        if(this.savebarEnabled){
          this.showSavebar = true;
        }
      },
      deep: true
    },
    'templateName': {
      handler(){
        if(this.savebarEnabled){
          this.showSavebar = true;
        }
      },
      deep: true
    },
    'isActive': {
      handler(){
        if(this.savebarEnabled){
          this.showSavebar = true;
        }
      },
    },
    'separator': {
      handler(){
        if(this.savebarEnabled){
          this.showSavebar = true;
        }
      },
      deep: true
    },
    'requiredFields': {
      handler(){
        if(this.savebarEnabled){
          this.showSavebar = true;
        }
      },
      deep: true
    },
    'additionalFieldsData': {
      handler(){
        if(this.savebarEnabled){
          this.showSavebar = true;
        }
      },
      deep: true
    },
    'startRow': {
      handler(){
        if(this.savebarEnabled){
          this.showSavebar = true;
        }
      },
      deep: true
    },
  },
}
</script>
<style scoped>
.loading-fields-text {
  line-height: 45px;
  color: #666;
  font-style: italic;
}

.fields-scroller {
  height: 600px;
  overflow-y: auto;
  overflow-x: visible;
  border: 1px solid #DBDFE3;
  border-radius: 4px;
  padding: 8px;
}

.fields-scroller >>> .vue-recycle-scroller__item-wrapper {
  overflow: visible;
}

.fields-scroller >>> .vue-recycle-scroller__item-view {
  overflow: visible;
}

.scroller-row {
  height: 65px;
  display: flex;
  align-items: center;
  padding-bottom: 10px;
  box-sizing: border-box;
}

.btn-delete-field {
  width: 36px;
  height: 36px;
  min-width: 36px;
  padding: 0;
}

.template-action-btns {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
}
</style>
